import React, { useEffect, useRef, useState } from 'react';
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaEraser,
  FaFont,
} from 'react-icons/fa';

interface RichTextEditorProps {
  /** Current HTML value (controlled by the parent). */
  value: string;
  /** Called with the new HTML whenever the user edits or formats the text. */
  onChange: (html: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  /** Optional ref to the editable area, e.g. for autofocus on modal open. */
  editorRef?: React.RefObject<HTMLDivElement | null>;
}

/* fontSize 1–7 maps to keyword sizes when styleWithCSS is on (xx-small … xx-large) */
const SIZES: { value: string; title: string; fs: string }[] = [
  { value: '3', title: 'Normal text', fs: '0.8rem' },
  { value: '5', title: 'Large text', fs: '1rem' },
  { value: '7', title: 'Title', fs: '1.2rem' },
];

const DEFAULT_COLOR = '#12263f';

/**
 * Lightweight WYSIWYG editor over a contentEditable div. Produces HTML the
 * backend already renders via dangerouslySetInnerHTML — no extra dependency.
 * Toolbar actions use onMouseDown + preventDefault so the text selection inside
 * the editor is never lost when a button is pressed.
 */
const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
  ariaLabel,
  editorRef,
}) => {
  const internalRef = useRef<HTMLDivElement>(null);
  const ref = editorRef ?? internalRef;
  /* last selection that lived inside the editor — kept up to date so the color
     picker can restore it after the native dialog steals focus (otherwise the
     selection collapses and foreColor would have nothing to act on). */
  const savedRange = useRef<Range | null>(null);
  const [currentColor, setCurrentColor] = useState(DEFAULT_COLOR);

  useEffect(() => {
    const trackSelection = () => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (ref.current?.contains(range.commonAncestorContainer)) {
        savedRange.current = range.cloneRange();
      }
    };
    document.addEventListener('selectionchange', trackSelection);
    return () =>
      document.removeEventListener('selectionchange', trackSelection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* sync external value in only when it differs from the live DOM — this fires
     when opening a different item but not on each keystroke, so the caret never
     jumps while typing (onChange feeds back the same HTML already in the DOM). */
  useEffect(() => {
    const el = ref.current;
    if (el && value !== el.innerHTML) {
      el.innerHTML = value || '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const exec = (command: string, val?: string) => {
    ref.current?.focus();
    document.execCommand('styleWithCSS', false, 'true');
    document.execCommand(command, false, val);
    emit();
  };

  const onToolMouseDown =
    (command: string, val?: string) => (e: React.MouseEvent) => {
      e.preventDefault();
      exec(command, val);
    };

  /* the native color input takes focus, so restore the tracked selection
     before applying the chosen color to it */
  const applyColor = (color: string) => {
    ref.current?.focus();
    const sel = window.getSelection();
    if (sel && savedRange.current) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
    document.execCommand('styleWithCSS', false, 'true');
    document.execCommand('foreColor', false, color);
    emit();
  };

  return (
    <div className="mgmt-rte">
      <div className="mgmt-rte__toolbar">
        <button
          type="button"
          className="mgmt-rte__btn"
          title="Bold"
          aria-label="Bold"
          onMouseDown={onToolMouseDown('bold')}
        >
          <FaBold size={12} />
        </button>
        <button
          type="button"
          className="mgmt-rte__btn"
          title="Italic"
          aria-label="Italic"
          onMouseDown={onToolMouseDown('italic')}
        >
          <FaItalic size={12} />
        </button>
        <button
          type="button"
          className="mgmt-rte__btn"
          title="Underline"
          aria-label="Underline"
          onMouseDown={onToolMouseDown('underline')}
        >
          <FaUnderline size={12} />
        </button>

        <span className="mgmt-rte__sep" />

        {SIZES.map(s => (
          <button
            key={s.value}
            type="button"
            className="mgmt-rte__btn"
            title={s.title}
            aria-label={s.title}
            style={{ fontSize: s.fs, lineHeight: 1 }}
            onMouseDown={onToolMouseDown('fontSize', s.value)}
          >
            A
          </button>
        ))}

        <span className="mgmt-rte__sep" />

        <label
          className="mgmt-rte__color"
          title="Text color — pick any color"
          onMouseDown={() => ref.current?.focus()}
        >
          <FaFont size={12} />
          <span
            className="mgmt-rte__color-bar"
            style={{ background: currentColor }}
          />
          <input
            type="color"
            className="mgmt-rte__color-input"
            aria-label="Text color"
            value={currentColor}
            onChange={e => {
              setCurrentColor(e.currentTarget.value);
              applyColor(e.currentTarget.value);
            }}
          />
        </label>

        <span className="mgmt-rte__sep" />

        <button
          type="button"
          className="mgmt-rte__btn"
          title="Clear formatting"
          aria-label="Clear formatting"
          onMouseDown={onToolMouseDown('removeFormat')}
        >
          <FaEraser size={12} />
        </button>
      </div>

      <div
        ref={ref}
        className="mgmt-rte__area"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        data-placeholder={placeholder}
        onInput={emit}
      />
    </div>
  );
};

export default RichTextEditor;
